import type { ImportTemplateType } from 'App/helpers/importTemplatePaths';
import { isOrderImportColumn } from 'App/helpers/orderImportTags';

export type ImportTagGroup = 'contact' | 'client' | 'product' | 'order' | 'orderRow';

type RequiredColumn = { tags: ReadonlyArray<string>; label: string };

type ImportTypeConfig = {
	tagGroups: ReadonlyArray<ImportTagGroup>;
	requiredColumns: ReadonlyArray<RequiredColumn>;
	hasUpdateOption: boolean;
	canOnlyCreate: boolean;
	onlyUpdateNeedsRequiredColumns: boolean;
	hasDeduplication: boolean;
	settingsDescription: string;
	acceptsColumn?: (field: string) => boolean;
};

const IMPORT_TYPE_CONFIG: Record<ImportTemplateType, ImportTypeConfig> = {
	CLIENT_AND_CONTACT: {
		tagGroups: ['contact', 'client'],
		requiredColumns: [{ tags: ['Client.Name'], label: 'tag.Client.name' }],
		hasUpdateOption: true,
		canOnlyCreate: false,
		onlyUpdateNeedsRequiredColumns: false,
		hasDeduplication: true,
		settingsDescription: 'import.importSettingsDesc'
	},
	PRODUCT: {
		tagGroups: ['product'],
		requiredColumns: [{ tags: ['Product.Name'], label: 'tag.product.name' }],
		hasUpdateOption: true,
		canOnlyCreate: false,
		onlyUpdateNeedsRequiredColumns: false,
		hasDeduplication: true,
		settingsDescription: 'import.importSettingsProductDesc'
	},
	ORDER: {
		tagGroups: ['order', 'orderRow', 'client'],
		requiredColumns: [
			{ tags: ['Order.Date'], label: 'import.order.date' },
			{ tags: ['Client.Id', 'Client.OrgNo', 'Client.Name'], label: 'import.order.requiredCompany' },
			{ tags: ['OrderRow.Product.ArticleNo', 'OrderRow.Product.Name'], label: 'import.order.requiredProduct' }
		],
		hasUpdateOption: true,
		canOnlyCreate: true,
		onlyUpdateNeedsRequiredColumns: true,
		hasDeduplication: false,
		settingsDescription: 'import.order.createOnly',
		acceptsColumn: isOrderImportColumn
	}
};

export const getImportTypeConfig = (type: string): ImportTypeConfig =>
	IMPORT_TYPE_CONFIG[type as ImportTemplateType] ?? IMPORT_TYPE_CONFIG.CLIENT_AND_CONTACT;

const TAG_GROUP_PREFIXES: ReadonlyArray<[string, ImportTagGroup]> = [
	['Product.', 'product'],
	['Contact.', 'contact'],
	['OrderRow.', 'orderRow'],
	['Order.', 'order']
];

export const tagGroupOf = (field: string): ImportTagGroup =>
	TAG_GROUP_PREFIXES.find(([prefix]) => field.startsWith(prefix))?.[1] ?? 'client';

export const missingRequiredColumns = (
	type: string,
	fields: ReadonlyArray<string>,
	onlyUpdate: boolean
): RequiredColumn[] => {
	const config = getImportTypeConfig(type);
	if (onlyUpdate && config.hasUpdateOption && !config.onlyUpdateNeedsRequiredColumns) {
		return [];
	}
	return config.requiredColumns.filter(required => !required.tags.some(tag => fields.includes(tag)));
};
